import { useState, useEffect } from "react";
import { IoMdCheckmark } from "react-icons/io";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import styles from "../../styles/__labDialogViewTechnician.module.scss";
import { useTranslation } from "react-i18next";
import { Edit2, Calendar, Clock } from "lucide-react";
import { getTechnicianAssignments } from "../../services/labTechniciansService";
import { toast } from "../toast";
import { format } from "date-fns";

export default function LabDialogViewTechnical({
  open,
  onClose,
  technician,
  onEdit,
}) {
  const [filterDate, setFilterDate] = useState(null);
  const [filterTime, setFilterTime] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [technicianDetails, setTechnicianDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  // Helper function to format location type
  const formatLocation = (type) => {
    if (type === "HOME_VISIT")
      return t("lab.technicianDialog.location.homeVisit");
    if (type === "LAB_VISIT")
      return t("lab.technicianDialog.location.labVisit");
    if (type === "ON_SITE") return t("lab.technicianDialog.location.onSite");
    return type || "-";
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString)
      return t("lab.technicianDialog.assignments.datePlaceholder");
    try {
      return format(new Date(dateString), "dd/MM/yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return t("lab.technicianDialog.assignments.datePlaceholder");
    }
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString)
      return t("lab.technicianDialog.assignments.timePlaceholder");
    try {
      // Handle time-only strings like "08:30:00"
      if (timeString.includes(":") && !timeString.includes("T")) {
        const [hours, minutes] = timeString.split(":");
        const date = new Date();
        date.setHours(parseInt(hours, 10));
        date.setMinutes(parseInt(minutes, 10));
        return format(date, "hh:mm aa");
      }
      // Handle full datetime strings
      return format(new Date(timeString), "hh:mm aa");
    } catch (error) {
      console.error("Error formatting time:", error);
      return t("lab.technicianDialog.assignments.timePlaceholder");
    }
  };

  // Fetch technician assignments
  const fetchAssignments = async () => {
    if (!technician?.id) return;

    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: 10,
      };

      // Only add date filters if user has set them
      if (filterDate) {
        params.date_from = format(filterDate, "yyyy-MM-dd");
      }
      if (filterTime) {
        // If time is set, we might want to filter by that specific day
        // For now, we'll just use the date part
        params.date_to = format(filterTime, "yyyy-MM-dd");
      }

      const response = await getTechnicianAssignments(technician.id, params);

      if (response && response.status) {
        // Bind the enhanced API response
        setAssignments(response.data?.assignments || []);
        setTechnicianDetails(response.data?.technician || null);
        setTotalPages(response.data?.pagination?.totalPages || 1);

        console.log("API Response:", response.data);
        console.log("Assignments:", response.data?.assignments);
        console.log("Technician Details:", response.data?.technician);
      } else {
        setAssignments([]);
        setTechnicianDetails(null);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching technician assignments:", error);
      toast.error(t("lab.technicianDialog.error.fetchAssignments"));
      setAssignments([]);
      setTechnicianDetails(null);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.body.classList.add("modal-open");
      // Reset state when opening
      setCurrentPage(1);
      setFilterDate(null);
      setFilterTime(null);
      setAssignments([]);
      setTechnicianDetails(null);
      // Fetch assignments when dialog opens
      fetchAssignments();
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [open, technician?.id]);

  // Fetch assignments when page changes or filters are applied
  useEffect(() => {
    if (open && technician?.id) {
      fetchAssignments();
    }
  }, [currentPage]);

  if (!open || !technician) return null;

  const handleClearFilters = () => {
    setFilterDate(null);
    setFilterTime(null);
    // Reset to page 1 and refetch assignments without date filters
    setCurrentPage(1);
    fetchAssignments();
  };

  const handleApplyFilters = () => {
    // Reset to page 1 and refetch assignments with current filter values
    setCurrentPage(1);
    fetchAssignments();
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const EditIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.70809 19.8987C5.25793 19.8987 4.87743 19.7432 4.56659 19.4324C4.25576 19.1216 4.10034 18.7411 4.10034 18.2909V5.70641C4.10034 5.25624 4.25576 4.87574 4.56659 4.56491C4.87743 4.25407 5.25793 4.09866 5.70809 4.09866H12.3351C12.5518 4.09866 12.7143 4.16674 12.8226 4.30291C12.9309 4.43924 12.9851 4.58782 12.9851 4.74866C12.9851 4.90949 12.9307 5.05807 12.8218 5.19441C12.713 5.33057 12.5539 5.39866 12.3446 5.39866H5.70809C5.63109 5.39866 5.56059 5.43074 5.49659 5.49491C5.43243 5.55891 5.40034 5.62941 5.40034 5.70641V18.2909C5.40034 18.3679 5.43243 18.4384 5.49659 18.5024C5.56059 18.5666 5.63109 18.5987 5.70809 18.5987H18.2926C18.3696 18.5987 18.4401 18.5666 18.5041 18.5024C18.5683 18.4384 18.6003 18.3679 18.6003 18.2909V12.2037C18.6003 11.9952 18.6684 11.8367 18.8046 11.7284C18.9409 11.6201 19.0895 11.5659 19.2503 11.5659C19.4112 11.5659 19.5598 11.6201 19.6961 11.7284C19.8323 11.8367 19.9003 11.9992 19.9003 12.2159V18.2909C19.9003 18.7411 19.7449 19.1216 19.4341 19.4324C19.1233 19.7432 18.7428 19.8987 18.2926 19.8987H5.70809ZM10.1003 13.0994V11.5722C10.1003 11.359 10.1388 11.1537 10.2158 10.9564C10.2927 10.7589 10.4103 10.581 10.5688 10.4227L18.8351 2.15641C18.9734 2.01791 19.1189 1.92141 19.2716 1.86691C19.4241 1.81241 19.584 1.78516 19.7513 1.78516C19.9208 1.78516 20.0838 1.81241 20.2403 1.86691C20.397 1.92141 20.5408 2.01291 20.6718 2.14141L21.8041 3.24866C21.9489 3.38716 22.0518 3.53899 22.1128 3.70416C22.1737 3.86932 22.2041 4.03674 22.2041 4.20641C22.2041 4.37641 22.1751 4.54099 22.1171 4.70016C22.0591 4.85932 21.9612 5.00749 21.8233 5.14466L13.5283 13.4399C13.3698 13.5982 13.1923 13.7143 12.9958 13.7882C12.7992 13.8618 12.5946 13.8987 12.3821 13.8987H10.9088C10.6813 13.8987 10.4898 13.8225 10.3341 13.6702C10.1783 13.5177 10.1003 13.3274 10.1003 13.0994ZM11.4003 12.5987H12.5216L18.7291 6.39091L18.1811 5.81791L17.5888 5.25066L11.4003 11.4392V12.5987Z"
        fill="#3B3B48"
      />
    </svg>
  );

  return (
    <div className={`${styles["labDialog_wrapper"]}`}>
      <div className={`${styles["dialog-overlay"]}`}>
        <div className={styles["dialog-box"]}>
          {/* Close Button (X) */}
          <button className={styles["dialog-close"]} onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.5525 12.0002L17.8525 7.71019C18.0408 7.52188 18.1466 7.26649 18.1466 7.00019C18.1466 6.73388 18.0408 6.47849 17.8525 6.29019C17.6642 6.10188 17.4088 5.99609 17.1425 5.99609C16.8762 5.99609 16.6208 6.10188 16.4325 6.29019L12.1425 10.5902L7.85252 6.29019C7.66421 6.10188 7.40882 5.99609 7.14252 5.99609C6.87622 5.99609 6.62082 6.10188 6.43252 6.29019C6.24421 6.47849 6.13843 6.73388 6.13843 7.00019C6.13843 7.26649 6.24421 7.52188 6.43252 7.71019L10.7325 12.0002L6.43252 16.2902C6.33879 16.3831 6.26439 16.4937 6.21363 16.6156C6.16286 16.7375 6.13672 16.8682 6.13672 17.0002C6.13672 17.1322 6.16286 17.2629 6.21363 17.3848C6.26439 17.5066 6.33879 17.6172 6.43252 17.7102C6.52548 17.8039 6.63608 17.8783 6.75794 17.9291C6.8798 17.9798 7.01051 18.006 7.14252 18.006C7.27453 18.006 7.40523 17.9798 7.52709 17.9291C7.64895 17.8783 7.75955 17.8039 7.85252 17.7102L12.1425 13.4102L16.4325 17.7102C16.5255 17.8039 16.6361 17.8783 16.7579 17.9291C16.8798 17.9798 17.0105 18.006 17.1425 18.006C17.2745 18.006 17.4052 17.9798 17.5271 17.9291C17.649 17.8783 17.7596 17.8039 17.8525 17.7102C17.9462 17.6172 18.0206 17.5066 18.0714 17.3848C18.1222 17.2629 18.1483 17.1322 18.1483 17.0002C18.1483 16.8682 18.1222 16.7375 18.0714 16.6156C18.0206 16.4937 17.9462 16.3831 17.8525 16.2902L13.5525 12.0002Z"
                fill="currentcolor"
              />
            </svg>
          </button>

          {/* Title Section with Edit Button */}
          <div className={styles["dialog-header"]}>
            <h2 className={styles["dialog-title"]}>
              {t("lab.technicianDialog.viewTitle")}
            </h2>
            <button
              className={styles["edit-btn"]}
              onClick={() => onEdit?.(technician)}
            >
              <EditIcon />
              {t("lab.technicianDialog.buttons.edit")}
            </button>
          </div>

          {/* Technician Information Grid */}
          <div className={styles["technician-info-grid"]}>
            <div className={styles["info-item"]}>
              <label>{t("lab.technicianDialog.form.name")}</label>
              <p className={styles["info-value"]}>
                {technicianDetails?.name ||
                  technician?.name ||
                  `${technician?.fullName || ""} ${technician?.surname || ""}`.trim() ||
                  "-"}
              </p>
            </div>

            <div className={styles["info-item"]}>
              <label>{t("lab.technicianDialog.form.gender")}</label>
              <p className={styles["info-value"]}>
                {technicianDetails?.gender || technician?.gender || "-"}
              </p>
            </div>

            <div className={styles["info-item"]}>
              <label>{t("lab.technicianDialog.form.mobileNumber")}</label>
              <p className={styles["info-value"]}>
                {technicianDetails?.mobileNumber ||
                  technician?.mobileNumber ||
                  "-"}
              </p>
            </div>

            {/* Availability / active status is intentionally not shown here. */}

            {/* Additional technician details from API */}
            {technicianDetails?.serviceCode && (
              <div className={styles["info-item"]}>
                <label>{t("lab.technicianDialog.view.serviceCode")}</label>
                <p className={styles["info-value"]}>
                  {technicianDetails.serviceCode}
                </p>
              </div>
            )}
          </div>

          {/* Current Assignment Section */}
          <div className={styles["assignment-section"]}>
            <h3 className={styles["section-title"]}>
              {t("lab.technicianDialog.view.currentAssignment")}
            </h3>

            {/* Filter Row */}
            <div className={styles["filter-row"]}>
              <div className={styles["filter-input-wrapper"]}>
                <Calendar className={styles["input-icon"]} size={16} />
                <DatePicker
                  selected={filterDate}
                  onChange={(date) => setFilterDate(date)}
                  placeholderText={t(
                    "lab.technicianDialog.assignments.datePlaceholder",
                  )}
                  dateFormat="dd/MM/yyyy"
                  className={styles["filter-input"]}
                />
              </div>

              <div className={styles["filter-input-wrapper"]}>
                <Clock className={styles["input-icon"]} size={16} />
                <DatePicker
                  selected={filterTime}
                  onChange={(time) => setFilterTime(time)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  placeholderText={t(
                    "lab.technicianDialog.assignments.timePlaceholder",
                  )}
                  className={styles["filter-input"]}
                />
              </div>

              <button
                className={styles["clear-btn"]}
                onClick={handleApplyFilters}
              >
                {t("lab.technicianDialog.buttons.apply")}
              </button>

              <button
                className={styles["clear-btn"]}
                onClick={handleClearFilters}
              >
                {t("lab.technicianDialog.buttons.clear")}
              </button>
            </div>

            {/* Assignments Table */}
            <div className={styles["modal-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>{t("lab.technicianDialog.assignments.table.date")}</th>
                    <th>{t("lab.technicianDialog.assignments.table.time")}</th>
                    <th>
                      {t("lab.technicianDialog.assignments.table.location")}
                    </th>
                    <th>
                      {t("lab.technicianDialog.assignments.table.patientName")}
                    </th>
                    <th>
                      {t("lab.technicianDialog.assignments.table.assignment")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        {t("lab.technicianDialog.assignments.loading")}
                      </td>
                    </tr>
                  ) : assignments.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        {t("lab.technicianDialog.assignments.noData")}
                      </td>
                    </tr>
                  ) : (
                    assignments.map((assignment, idx) => (
                      <tr key={assignment.id || assignment.bookingId || idx}>
                        <td>{formatDate(assignment.scheduledDate)}</td>
                        <td>{formatTime(assignment.scheduledTime)}</td>
                        <td>{formatLocation(assignment.locationType)}</td>
                        <td>{assignment.patientName || "-"}</td>
                        <td>
                          {assignment.testTypes
                            ? assignment.testTypes.join(", ")
                            : "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination for assignments */}
            {totalPages > 1 && (
              <div className={styles["assignment-pagination"]}>
                <div className={styles["pagination-controls"]}>
                  <button
                    className={`${styles["pagination-btn"]} ${currentPage === 1 ? styles["disabled"] : ""}`}
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    {t("lab.technicianDialog.buttons.previous")}
                  </button>
                  <div className={styles["page-numbers"]}>
                    {Array.from({ length: totalPages }, (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          className={`${styles["page-btn"]} ${currentPage === page ? styles["active"] : ""}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    className={`${styles["pagination-btn"]} ${currentPage === totalPages ? styles["disabled"] : ""}`}
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    {t("lab.technicianDialog.buttons.next")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
